import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { membersApi, settingsApi } from '@/lib/api';
import { Plus, Edit, Trash2, User, X, Settings } from 'lucide-react';
import { toast } from 'sonner';

const AdminMembers = () => {
  const [members, setMembers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', photo_url: '', photo_data: '', custom_fields: {}
  });
  const [newField, setNewField] = useState({ field_name: '', field_type: 'text', is_required: false });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [membersData, fieldsData] = await Promise.all([
        membersApi.getAll(),
        settingsApi.getCustomFields().catch(() => [])
      ]);
      setMembers(membersData);
      setCustomFields(fieldsData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', address: '', photo_url: '', photo_data: '', custom_fields: {} });
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name, email: item.email || '', phone: item.phone || '',
      address: item.address || '', photo_url: item.photo_url || '',
      photo_data: item.photo_data || '', custom_fields: item.custom_fields || {}
    });
    setDialogOpen(true);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => setFormData({ ...formData, photo_data: e.target.result, photo_url: '' });
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a name');
      return;
    }

    try {
      if (editingItem) {
        await membersApi.update(editingItem.id, formData);
        toast.success('Member updated');
      } else {
        await membersApi.create(formData);
        toast.success('Member added');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this member?')) return;
    try {
      await membersApi.delete(id);
      toast.success('Deleted');
      loadData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleAddField = async () => {
    if (!newField.field_name) {
      toast.error('Please enter a field name');
      return;
    }
    try {
      await settingsApi.addCustomField(newField);
      toast.success('Custom field added');
      setNewField({ field_name: '', field_type: 'text', is_required: false });
      setFieldDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleDeleteField = async (fieldName) => {
    if (!window.confirm(`Delete custom field "${fieldName}"?`)) return;
    try {
      await settingsApi.deleteCustomField(fieldName);
      toast.success('Field deleted');
      loadData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Member Directory</h1>
          <p className="text-stone-600">Manage church members</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline"><Settings className="h-4 w-4 mr-2" /> Custom Fields</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Custom Fields</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  {customFields.length > 0 ? (
                    customFields.map((field) => (
                      <div key={field.field_name} className="flex items-center justify-between p-2 bg-stone-50 rounded">
                        <span>{field.field_name} ({field.field_type})</span>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteField(field.field_name)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p className="text-stone-500 text-center py-2">No custom fields</p>
                  )}
                </div>
                <div className="border-t pt-4">
                  <Label>Add New Field</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newField.field_name}
                      onChange={(e) => setNewField({ ...newField, field_name: e.target.value })}
                      placeholder="Field name"
                    />
                    <Select value={newField.field_type} onValueChange={(v) => setNewField({ ...newField, field_type: v })}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddField}>Add</Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-700 hover:bg-amber-800"><Plus className="h-4 w-4 mr-2" /> Add Member</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingItem ? 'Edit Member' : 'Add Member'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </div>
                <div>
                  <Label>Photo</Label>
                  {formData.photo_data || formData.photo_url ? (
                    <div className="relative inline-block mt-2">
                      <img src={formData.photo_data || formData.photo_url} alt="Preview" className="h-20 w-20 rounded-full object-cover" />
                      <Button type="button" size="sm" variant="destructive" className="absolute -top-2 -right-2 h-6 w-6 p-0" onClick={() => setFormData({ ...formData, photo_data: '', photo_url: '' })}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <Input type="file" accept="image/*" onChange={handlePhotoUpload} className="mt-1" />
                  )}
                </div>
                {/* Custom Fields */}
                {customFields.length > 0 && (
                  <div className="border-t pt-4">
                    <Label className="text-stone-500">Custom Fields</Label>
                    <div className="space-y-3 mt-2">
                      {customFields.map((field) => (
                        <div key={field.field_name}>
                          <Label>{field.field_name}</Label>
                          <Input
                            type={field.field_type === 'date' ? 'date' : field.field_type === 'email' ? 'email' : 'text'}
                            value={formData.custom_fields[field.field_name] || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              custom_fields: { ...formData.custom_fields, [field.field_name]: e.target.value }
                            })}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                  <Button type="submit" className="bg-amber-700 hover:bg-amber-800">{editingItem ? 'Update' : 'Add'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto"></div>
        </CardContent></Card>
      ) : members.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Address</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden">
                        {item.photo_data || item.photo_url ? (
                          <img src={item.photo_data || item.photo_url} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="h-5 w-5 text-stone-400" />
                        )}
                      </div>
                      <span className="font-medium">{item.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.email && <div className="text-sm">{item.email}</div>}
                    {item.phone && <div className="text-sm text-stone-500">{item.phone}</div>}
                  </TableCell>
                  <TableCell>{item.address}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card><CardContent className="p-8 text-center">
          <User className="h-12 w-12 text-stone-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-800 mb-2">No Members</h3>
          <Button onClick={() => setDialogOpen(true)} className="bg-amber-700 hover:bg-amber-800"><Plus className="h-4 w-4 mr-2" /> Add Member</Button>
        </CardContent></Card>
      )}
    </div>
  );
};

export default AdminMembers;
